import styled from 'styled-components';
import SkeletonCardData from './skeletonCardData';
import { SkeletonLine } from './skeletonLine';

/**
 * Repeats PrimaryCard's box. Real cards colour their border by status, which is
 * unknown while loading, so the placeholder takes a neutral one — same 1px box,
 * no implied status.
 */
const SkeletonCard = styled.div`
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid var(--clr-border-grey-100);
  background-color: var(--clr-container-bg);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 10px;
`;

const IconGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderRule = styled.div`
  height: 1px;
  background-color: var(--clr-border-grey-100);
  margin-bottom: 10px;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.div<{ $columns: number }>`
  display: flex;
  gap: ${({ $columns }) => ($columns > 1 ? '10px' : '0')};

  /* Columns split the row evenly and shrink with it, so a two-column row stays
     inside the card however narrow the task pane gets. */
  > * {
    flex: 1 1 0;
    min-width: 0;
  }
`;

interface SkeletonEntityCardProps {
  /** One entry per row; the value is how many CardData blocks sit side by side. */
  rows: number[];
  withSubcontentOnFirstRow?: boolean;
  /** Off for cards that carry no status chip, such as notification and worktime cards. */
  withHeader?: boolean;
  /** How many round icon buttons the real card's header carries. */
  actionCount?: number;
  className?: string;
}

/** Most card headers carry three round icon buttons — copy, edit and go-to-detail. */
const DEFAULT_ACTION_COUNT = 3;

const buildActionKey = (index: number) => `skeleton-action-${index}`;
const buildRowKey = (columns: number, index: number) => `skeleton-row-${index}-${columns}`;
const buildColumnKey = (rowIndex: number, columnIndex: number) =>
  `skeleton-column-${rowIndex}-${columnIndex}`;

const SkeletonEntityCard = ({
  rows,
  withSubcontentOnFirstRow = false,
  withHeader = true,
  actionCount = DEFAULT_ACTION_COUNT,
  className = '',
}: SkeletonEntityCardProps) => (
  <SkeletonCard data-testid="skeleton-entity-card" className={className}>
    {withHeader && (
      <div data-testid="skeleton-card-header">
        <HeaderRow>
          <SkeletonLine $width="80px" $height="22px" $radius="30px" />
          <IconGroup>
            {Array.from({ length: actionCount }, (_, index) => (
              <SkeletonLine
                key={buildActionKey(index)}
                data-testid="skeleton-card-action"
                $width="18px"
                $height="18px"
                $radius="50%"
              />
            ))}
          </IconGroup>
        </HeaderRow>
        <HeaderRule />
      </div>
    )}
    <Rows>
      {rows.map((columns, rowIndex) => (
        <Row key={buildRowKey(columns, rowIndex)} $columns={columns}>
          {Array.from({ length: columns }, (_, columnIndex) => (
            <SkeletonCardData
              key={buildColumnKey(rowIndex, columnIndex)}
              withSubcontent={withSubcontentOnFirstRow && rowIndex === 0 && columnIndex === 0}
            />
          ))}
        </Row>
      ))}
    </Rows>
  </SkeletonCard>
);

export default SkeletonEntityCard;
